import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../../hooks/redux';
import { showToast } from '../../../store/slices/toastSlice';
import {
  Ll144PositionLocation,
  Ll144Role,
  Ll144SubjectType,
  submitLl144Assessment
} from '../../../services/Ll144AssessmentService';
import Banner from '../../ui/Banner/Banner';
import RadioButtonGroup from '../../ui/RadioButton/RadioButtonGroup';
import { Input } from '../../ui/Input/Input';
import { PrimaryButton } from '../../ui/Button/Buttons';
import { Button } from '../../ui/Button/Button';
import { WizardSteps, WizardStep } from '../../steps/WizardSteps';
import { Ll144QuestionCard } from './Ll144QuestionCard';
import {
  Ll144AnswerMap,
  Ll144QuestionKey,
  ll144Parts,
  positionLocationOptions,
  questionGuidance,
  roleOptions,
  subjectTypeOptions,
  visibleQuestionKeys,
  visibleQuestionKeysForPart,
  yesNoOptions
} from './Ll144Assessment.constants';

type Ll144AssessmentProps = {
  aiSystemId: string;
};

type YesNo = 'yes' | 'no' | '';

export function Ll144Assessment({ aiSystemId }: Ll144AssessmentProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [role, setRole] = useState<Ll144Role | ''>('');
  const [subjectType, setSubjectType] = useState<Ll144SubjectType | ''>('');
  const [usesAedt, setUsesAedt] = useState<YesNo>('');
  const [substantialAssistance, setSubstantialAssistance] =
    useState<YesNo>('');
  const [positionLocation, setPositionLocation] = useState<
    Ll144PositionLocation | ''
  >('');
  const [agencyLocatedInNyc, setAgencyLocatedInNyc] = useState<YesNo>('');
  const [assessedPeopleResideInNyc, setAssessedPeopleResideInNyc] =
    useState<YesNo>('');
  // YYYY-MM-DD, '' while unanswered — the same convention every other answer uses, which is what
  // lets the shared answeredCount/isComplete logic treat it like any other question.
  const [aedtFirstUseDate, setAedtFirstUseDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [activePart, setActivePart] = useState(0);

  // One record drives every count and completeness check, so adding a question cannot
  // leave a stale enumeration behind. Every answer is a string with '' meaning unanswered.
  const answers: Ll144AnswerMap = useMemo(
    () => ({
      role,
      subjectType,
      usesAedt,
      substantialAssistance,
      positionLocation,
      agencyLocatedInNyc,
      assessedPeopleResideInNyc,
      aedtFirstUseDate
    }),
    [
      role,
      subjectType,
      usesAedt,
      substantialAssistance,
      positionLocation,
      agencyLocatedInNyc,
      assessedPeopleResideInNyc,
      aedtFirstUseDate
    ]
  );

  // Only the three answers visibility actually turns on, so typing a date does not invalidate this.
  const visibilityAnswers = useMemo(
    () => ({ role, usesAedt, assessedPeopleResideInNyc }),
    [role, usesAedt, assessedPeopleResideInNyc]
  );

  const visibleKeys = useMemo(
    () => visibleQuestionKeys(visibilityAnswers),
    [visibilityAnswers]
  );

  const isVisible = (key: Ll144QuestionKey) => visibleKeys.includes(key);

  const answeredCount = useMemo(
    () => visibleKeys.filter((key) => answers[key]).length,
    [visibleKeys, answers]
  );

  const isComplete = useMemo(
    () => visibleKeys.every((key) => answers[key]),
    [visibleKeys, answers]
  );

  const handleSubmit = async () => {
    if (!isComplete || submitting) return;

    setSubmitting(true);
    try {
      await submitLl144Assessment(aiSystemId, {
        role: role as Ll144Role,
        subjectType: subjectType as Ll144SubjectType,
        usesAedt: usesAedt === 'yes',
        substantialAssistanceOrReplacement: substantialAssistance === 'yes',
        positionLocation: positionLocation as Ll144PositionLocation,
        assessedPeopleResideInNyc: assessedPeopleResideInNyc === 'yes',
        // Derived from the same predicate that decides whether to render the question, so
        // an answer left behind by switching role back to employer is never submitted.
        // The API rejects unknown properties, so omit the key rather than sending
        // undefined.
        ...(isVisible('agencyLocatedInNyc')
          ? { agencyLocatedInNyc: agencyLocatedInNyc === 'yes' }
          : {}),
        // Same reasoning: going back and answering "no" to usesAedt or to the NYC-residency
        // question must not leave a date behind in the payload. Omitted rather than sent as null,
        // because absence means PRESERVE server-side while null would CLEAR a stored date.
        ...(isVisible('aedtFirstUseDate') ? { aedtFirstUseDate } : {})
      });

      dispatch(
        showToast({ message: 'Assessment submitted.', type: 'success' })
      );
      navigate(`/ai-systems/${aiSystemId}/requirement-list`);
    } catch (error) {
      // The API interceptor surfaces the error toast; just stop submitting.
      console.error('Failed to submit LL144 assessment:', error);
      setSubmitting(false);
    }
  };

  // A part every one of whose questions is hidden is dropped entirely, rather than rendered as an
  // empty step the user has to click past. Today that is only "When the tool is used", which is
  // skipped for a non-AEDT tool or one that screens nobody resident in New York City.
  //
  // `activePart` indexes THIS list, so a part disappearing while the user is on a later one would
  // leave the index dangling — hence the clamp below.
  const visibleParts = useMemo(
    () =>
      ll144Parts.filter(
        (part) => visibleQuestionKeysForPart(part, visibilityAnswers).length > 0
      ),
    [visibilityAnswers]
  );

  const safeActivePart = Math.min(activePart, visibleParts.length - 1);
  const activePartDef = visibleParts[safeActivePart];
  const isLastPart = safeActivePart === visibleParts.length - 1;
  const visiblePartKeys = visibleQuestionKeysForPart(
    activePartDef,
    visibilityAnswers
  );
  const isPartComplete = visiblePartKeys.every((key) => answers[key]);

  const steps: WizardStep[] = visibleParts.map((part, index) => ({
    id: part.id,
    // Renumbered against the visible list: skipping a part must not leave a gap in the stepper.
    number: index + 1,
    title: part.title,
    status:
      index < safeActivePart
        ? 'complete'
        : index === safeActivePart
          ? 'current'
          : 'upcoming'
  }));

  const goNext = () =>
    setActivePart(() => Math.min(visibleParts.length - 1, safeActivePart + 1));
  const goBack = () => setActivePart(() => Math.max(0, safeActivePart - 1));

  const renderQuestionCard = (key: Ll144QuestionKey) => {
    switch (key) {
      case 'role':
        return (
          <Ll144QuestionCard
            key={key}
            className="mx-12"
            title="What is your role?"
            description={questionGuidance.role.description}
            hint={questionGuidance.role.hint}
            definitions={questionGuidance.role.definitions}
          >
            <RadioButtonGroup
              name="ll144-role"
              options={roleOptions}
              selectedValue={role}
              onChange={(value) => setRole(value as Ll144Role)}
            />
          </Ll144QuestionCard>
        );
      case 'subjectType':
        return (
          <Ll144QuestionCard
            key={key}
            className="mx-12"
            title="Who is assessed by the tool?"
            description={questionGuidance.subjectType.description}
            hint={questionGuidance.subjectType.hint}
            definitions={questionGuidance.subjectType.definitions}
          >
            <RadioButtonGroup
              name="ll144-subject-type"
              options={subjectTypeOptions}
              selectedValue={subjectType}
              onChange={(value) => setSubjectType(value as Ll144SubjectType)}
            />
          </Ll144QuestionCard>
        );
      case 'usesAedt':
        return (
          <Ll144QuestionCard
            key={key}
            className="mx-12"
            title="Does the system use an Automated Employment Decision Tool (AEDT)?"
            description={questionGuidance.usesAedt.description}
            hint={questionGuidance.usesAedt.hint}
            definitions={questionGuidance.usesAedt.definitions}
          >
            <RadioButtonGroup
              name="ll144-uses-aedt"
              options={yesNoOptions}
              selectedValue={usesAedt}
              onChange={(value) => setUsesAedt(value as YesNo)}
              direction="horizontal"
            />
          </Ll144QuestionCard>
        );
      case 'substantialAssistance':
        return (
          <Ll144QuestionCard
            key={key}
            className="mx-12"
            title="Does the tool substantially assist or replace discretionary decision-making?"
            description={questionGuidance.substantialAssistance.description}
            hint={questionGuidance.substantialAssistance.hint}
            definitions={questionGuidance.substantialAssistance.definitions}
          >
            <RadioButtonGroup
              name="ll144-substantial-assistance"
              options={yesNoOptions}
              selectedValue={substantialAssistance}
              onChange={(value) => setSubstantialAssistance(value as YesNo)}
              direction="horizontal"
            />
          </Ll144QuestionCard>
        );
      case 'positionLocation':
        return (
          <Ll144QuestionCard
            key={key}
            className="mx-12"
            title="Where are the positions this tool is used to screen for?"
            description={questionGuidance.positionLocation.description}
            hint={questionGuidance.positionLocation.hint}
            definitions={questionGuidance.positionLocation.definitions}
          >
            <RadioButtonGroup
              name="ll144-position-location"
              options={positionLocationOptions}
              selectedValue={positionLocation}
              onChange={(value) =>
                setPositionLocation(value as Ll144PositionLocation)
              }
            />
          </Ll144QuestionCard>
        );
      case 'agencyLocatedInNyc':
        return (
          <Ll144QuestionCard
            key={key}
            className="mx-12"
            title="Is your employment agency located in New York City?"
            description={questionGuidance.agencyLocatedInNyc.description}
            hint={questionGuidance.agencyLocatedInNyc.hint}
            definitions={questionGuidance.agencyLocatedInNyc.definitions}
          >
            <RadioButtonGroup
              name="ll144-agency-located-in-nyc"
              options={yesNoOptions}
              selectedValue={agencyLocatedInNyc}
              onChange={(value) => setAgencyLocatedInNyc(value as YesNo)}
              direction="horizontal"
            />
          </Ll144QuestionCard>
        );
      case 'assessedPeopleResideInNyc':
        return (
          <Ll144QuestionCard
            key={key}
            className="mx-12"
            title="Do any of the assessed candidates or employees reside in New York City?"
            description={questionGuidance.assessedPeopleResideInNyc.description}
            hint={questionGuidance.assessedPeopleResideInNyc.hint}
            definitions={
              questionGuidance.assessedPeopleResideInNyc.definitions
            }
          >
            <RadioButtonGroup
              name="ll144-assessed-people-reside-in-nyc"
              options={yesNoOptions}
              selectedValue={assessedPeopleResideInNyc}
              onChange={(value) =>
                setAssessedPeopleResideInNyc(value as YesNo)
              }
              direction="horizontal"
            />
          </Ll144QuestionCard>
        );
      case 'aedtFirstUseDate':
        return (
          <Ll144QuestionCard
            key={key}
            className="mx-12"
            title="When is the tool first used to assess candidates or employees?"
            description={questionGuidance.aedtFirstUseDate.description}
            hint={questionGuidance.aedtFirstUseDate.hint}
            definitions={questionGuidance.aedtFirstUseDate.definitions}
          >
            <Input
              id="ll144-aedt-first-use-date"
              type="date"
              label="First use date"
              value={aedtFirstUseDate}
              maxLength={10}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setAedtFirstUseDate(event.target.value)
              }
            />
          </Ll144QuestionCard>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-4 mx-12">
        <p className="text-sm leading-5 text-gray-550">
          Answer the following questions to determine whether this system is in
          scope of NYC Local Law 144 and to generate its obligations.
        </p>
        <Banner
          text="Results are based on the information provided and current NYC Local Law 144 documentation. This assessment does not constitute legal advice."
          backgroundColorClass="bg-blue-50"
          titleColorClass="text-purple-900"
          textColorClass="text-purple-900"
          className="w-full"
        />
      </div>

      <WizardSteps steps={steps} />

      <h2 className="mx-12 my-5 text-base font-medium text-left text-gray-900">
        {activePartDef.title}
      </h2>

      {visiblePartKeys.map((key) => renderQuestionCard(key))}

      <div className="sticky bottom-0 z-10 flex justify-between pt-5 bg-white border-t border-gray-150">
        <Button
          onClick={goBack}
          buttonClassName={`flex items-center py-[9px] px-[17px] h-[38px] text-gray-700 text-sm font-medium border border-gray-300 rounded-full shadow-sm hover:bg-gray-250 hover:text-gray-700 outline-none ${
            activePart === 0 ? 'hidden' : ''
          }`}
        >
          Back
        </Button>
        <div className="flex items-center gap-4 ml-auto">
          <span className="text-sm leading-5 text-gray-550">
            {answeredCount} of {visibleKeys.length} answered
          </span>
          <PrimaryButton
            onClick={isLastPart ? handleSubmit : goNext}
            disabled={
              isLastPart ? !isComplete || submitting : !isPartComplete
            }
          >
            {isLastPart
              ? submitting
                ? 'Submitting...'
                : 'Submit Assessment'
              : 'Next'}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}
